import React, { useState, useCallback } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, FolderPlus, AlertCircle } from 'lucide-react';

interface SubcategoryModalProps {
  isOpen: boolean;
  categoryName: string;
  onClose: () => void;
  onSave: (subcategoryName: string) => void;
  existingSubcategories?: string[];
}

export const SubcategoryModal: React.FC<SubcategoryModalProps> = ({
  isOpen,
  categoryName,
  onClose,
  onSave,
  existingSubcategories = []
}) => {
  const [subcategoryName, setSubcategoryName] = useState('');
  const [error, setError] = useState('');
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isPrimaryHovered, setIsPrimaryHovered] = useState(false);
  const [isSecondaryHovered, setIsSecondaryHovered] = useState(false);

  const handleClose = useCallback(() => {
    setSubcategoryName('');
    setError('');
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = subcategoryName.trim();

    // Validation
    if (!trimmedName) {
      setError('Subcategory name is required');
      return;
    }

    if (trimmedName.toLowerCase() === 'items') {
      setError('"Items" is a reserved name. Please choose a different name.');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Subcategory name must be at least 2 characters long');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Subcategory name must be less than 50 characters');
      return;
    }

    if (existingSubcategories.some(sub => sub.toLowerCase() === trimmedName.toLowerCase())) {
      setError(`Subcategory "${trimmedName}" already exists`);
      return;
    }

    onSave(trimmedName);
    handleClose();
  };

  return (
    <div style={styles.modal.overlay}>
      <div style={styles.modal.containerCompact}>
        <button
          onClick={handleClose}
          onMouseEnter={() => setIsCloseHovered(true)}
          onMouseLeave={() => setIsCloseHovered(false)}
          style={{
            ...styles.modal.close,
            ...(isCloseHovered ? styles.modal.closeHover : {})
          }}
        >
          <X size={20} />
        </button>

        <div style={styles.modal.header}>
          <div style={styles.modal.icon}>
            <FolderPlus size={20} style={{ color: 'white' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={styles.modal.title}>Add Subcategory</h2>
            <p style={styles.modal.subtitle}>Under '{categoryName}'</p>
          </div>
        </div>

        <div style={styles.modal.body}>
          <p style={styles.modal.description}>
            Create a subcategory under {categoryName}.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Subcategory Name
            </label>
            <input
              type="text"
              value={subcategoryName}
              onChange={(e) => {
                setSubcategoryName(e.target.value);
                setError('');
              }}
              style={styles.form.input}
              placeholder="e.g., Documentation, Resources, Archive..."
              autoFocus
            />
            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
                <p style={styles.form.error}>{error}</p>
              </div>
            )}
          </div>

          <div style={styles.modal.tip}>
            <strong style={styles.modal.tipStrong}>Tip:</strong> Once created, add items using the plus icon.
          </div>

          <div style={styles.modal.footer}>
            <button
              type="button"
              onClick={handleClose}
              onMouseEnter={() => setIsSecondaryHovered(true)}
              onMouseLeave={() => setIsSecondaryHovered(false)}
              style={{
                ...styles.button.base,
                ...styles.button.secondary,
                ...(isSecondaryHovered ? styles.button.secondaryHoverEffect : {})
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              onMouseEnter={() => setIsPrimaryHovered(true)}
              onMouseLeave={() => setIsPrimaryHovered(false)}
              style={{
                ...styles.button.base,
                ...styles.button.primary,
                ...(isPrimaryHovered ? styles.button.primaryHoverEffect : {})
              }}
            >
              Create Subcategory
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};